import * as Popover from "@radix-ui/react-popover";
import { Check, Monitor, Moon, Sun } from "lucide-react";
import { ReactNode } from "react";
import { cn } from "../../../utils/classes";
import { THEME_MODE_OPTIONS, ThemeMode, useTheme } from "../../../utils/theme";
// Reuses `<NavRailItem>`'s own icon-box/label classes for the trigger button
// (rather than duplicating near-identical rules here) so the theme toggle is
// visually and structurally identical to every other rail item — same
// 40x40 collapsed icon square, same label position/size, same hover/active
// treatment, same expanded-row layout, automatically staying in sync with
// nav-rail.module.css instead of two copies drifting apart.
import railStyles from "../nav-rail/nav-rail.module.css";
import styles from "./theme-toggle.module.css";

// Keyed off `ThemeMode` so adding a 4th mode to `THEME_MODE_OPTIONS` forces a
// matching icon here (a type error otherwise) instead of relying on
// hardcoded JSX branches per mode.
const MODE_ICONS: Record<ThemeMode, ReactNode> = {
  system: <Monitor />,
  light: <Sun />,
  dark: <Moon />,
};

export type ThemeToggleProps = {
  /** Mirrors the owning `<NavRail>`'s expanded state — hides the label when collapsed. */
  expanded?: boolean;
  className?: string;
};

/**
 * Theme switcher popover anchored to the bottom of the nav rail, offering
 * System / Light / Dark (`THEME_MODE_OPTIONS`), wired to `useTheme()`. Its
 * trigger reuses `<NavRailItem>`'s own classes so it's visually identical to
 * every other rail item — but unlike a real nav item, it deliberately shows
 * NO label when collapsed (icon only); `expanded` gates that explicitly in
 * JS rather than via CSS, since cross-file CSS Modules can't reliably target
 * an ancestor class defined in a different module.
 */
export function ThemeToggle({ expanded = false, className }: ThemeToggleProps) {
  const { mode, setMode } = useTheme();
  const activeOption =
    THEME_MODE_OPTIONS.find((option) => option.value === mode) ??
    THEME_MODE_OPTIONS[0];

  // Smooth RADIAL reveal when switching theme, via the browser's native View
  // Transitions API (same base technique used by a sibling app in this
  // monorepo's own theme toggle, extended here with a circular clip-path
  // instead of the default crossfade — see tokens.css's
  // ::view-transition-new(root) rule for the actual animation). The new
  // frame expands outward from wherever the option was clicked: set that
  // point as CSS custom properties on <html> BEFORE starting the
  // transition, so the global CSS can center the circle() on it. Falls back
  // to a plain, un-animated update in browsers that don't support the API
  // yet (e.g. Firefox).
  const selectMode = (
    next: ThemeMode,
    origin?: { x: number; y: number },
  ) => {
    if (typeof document === "undefined" || !("startViewTransition" in document)) {
      setMode(next);
      return;
    }
    const root = document.documentElement;
    root.style.setProperty("--theme-transition-x", `${origin?.x ?? window.innerWidth / 2}px`);
    root.style.setProperty("--theme-transition-y", `${origin?.y ?? window.innerHeight / 2}px`);
    // `themeTransition` opts INTO the circular-reveal keyframe in
    // tokens.css (scoped there under `html.themeTransition`) — every OTHER
    // view transition in the app (page navigation via nav rail/breadcrumbs/
    // row links) shares the same ::view-transition-*(root) pseudo-elements
    // but gets tokens.css's plain default crossfade instead, since they
    // don't add this class.
    root.classList.add("themeTransition");
    const transition = document.startViewTransition(() => setMode(next));
    transition.finished.finally(() => {
      root.classList.remove("themeTransition");
    });
  };

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            railStyles.item,
            styles.trigger,
            !expanded && styles.triggerCollapsed,
            className,
          )}
          aria-label={`Theme: ${activeOption.label}`}
          title={`Theme: ${activeOption.label}`}
        >
          <span className={railStyles.itemIconBox}>
            <span className={railStyles.itemIcon}>
              {MODE_ICONS[activeOption.value]}
            </span>
          </span>
          {expanded && (
            <span className={railStyles.itemLabel}>{activeOption.label}</span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="right"
          align="end"
          sideOffset={8}
          className={styles.content}
        >
          <ul className={styles.list} role="listbox">
            {THEME_MODE_OPTIONS.map((option) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === mode}
                  className={cn(
                    styles.option,
                    option.value === mode && styles.optionActive,
                  )}
                  onClick={(event) =>
                    selectMode(option.value, {
                      x: event.clientX,
                      y: event.clientY,
                    })
                  }
                >
                  <span className={styles.optionIcon}>
                    {MODE_ICONS[option.value]}
                  </span>
                  <span className={styles.optionLabel}>{option.label}</span>
                  {option.value === mode && (
                    <Check className={styles.checkIcon} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
